/**
 * ProcessingStatsTab — "Processing Stats" tab content for LakeFileDetail.
 *
 * Layout:
 *   Top:    Radial Chart - Shape cards for each confidence score (Layout, OCR, Parse, Table)
 *   Middle: Document-level stat boxes + grade pills
 *   Bottom: Paginated pages table (25/page) — row click opens PageDetailSidebar
 */

import { AlertCircle, BarChart3, ChevronLeft, ChevronRight, Info } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { GradeChip, ScoreShapeCard } from '@/components/ui/chart'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type {
  DocumentStatsResponse,
  PageStatsListResponse,
  PageStatsSummary,
} from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { formatDocumentType } from '@/utils/document-labels'
import { PageDetailSidebar } from './PageDetailSidebar'

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined, fallback = '—'): string {
  if (n == null) return fallback
  return n.toLocaleString()
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—'
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

/** Small info icon that shows a tooltip on hover. */
function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-default">
          <Info className="h-3 w-3 text-muted-foreground/50 hover:text-muted-foreground transition-colors" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-center leading-snug">
        {text}
      </TooltipContent>
    </Tooltip>
  )
}

function StatBox({ label, value, tip }: { label: string; value: string | number; tip?: string }) {
  return (
    <div className="bg-muted rounded p-2 flex flex-col gap-0.5">
      <span className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
        {tip && <InfoTip text={tip} />}
      </span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  )
}

// ─── component ───────────────────────────────────────────────────────────────

interface Props {
  documentId: string
}

export function ProcessingStatsTab({ documentId }: Props) {
  const [docStats, setDocStats] = useState<DocumentStatsResponse | null>(null)
  const [docLoading, setDocLoading] = useState(true)
  const [docError, setDocError] = useState<string | null>(null)

  const [pages, setPages] = useState<PageStatsSummary[]>([])
  const [pagesTotal, setPagesTotal] = useState(0)
  const [pagesTotalPages, setPagesTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagesLoading, setPagesLoading] = useState(false)
  const [pagesError, setPagesError] = useState<string | null>(null)

  const [selectedPage, setSelectedPage] = useState<number | null>(null)

  // ── fetch document-level stats ──
  useEffect(() => {
    let cancelled = false
    setDocLoading(true)
    setDocError(null)
    lakeApi
      .getProcessingStats(documentId)
      .then(data => {
        if (!cancelled) setDocStats(data)
      })
      .catch(() => {
        if (!cancelled) setDocError('Processing stats not available for this document.')
      })
      .finally(() => {
        if (!cancelled) setDocLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [documentId])

  // ── fetch pages ──
  const fetchPages = useCallback(
    (page: number) => {
      setPagesLoading(true)
      setPagesError(null)
      lakeApi
        .getProcessingStatsPages(documentId, page, 25)
        .then((data: PageStatsListResponse) => {
          setPages(data.items)
          setPagesTotal(data.total)
          setPagesTotalPages(data.total_pages)
        })
        .catch(() => setPagesError('Failed to load page stats.'))
        .finally(() => setPagesLoading(false))
    },
    [documentId]
  )

  useEffect(() => {
    fetchPages(currentPage)
  }, [fetchPages, currentPage])

  // ─── render ───────────────────────────────────────────────────────────────

  if (docLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (docError || !docStats) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
        <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          {docError ?? 'No processing stats available.'}
        </p>
        <p className="text-xs text-muted-foreground/70">
          Stats are generated when a document is processed through Phase 1.
        </p>
      </div>
    )
  }

  const hasConfidence = docStats.layout_score != null || docStats.ocr_score != null

  const visionStats = docStats.stats as Record<string, unknown> | null
  const isVisionLLM =
    typeof visionStats?.extractor === 'string' &&
    (visionStats.extractor as string).includes('vision')
  const isAudio = visionStats?.extractor === 'whisperx'

  const scoreCards = isVisionLLM
    ? [{ label: 'Confidence', score: docStats.layout_score, colorVar: 'chart-1' as const }]
    : [
        { label: 'Layout', score: docStats.layout_score, colorVar: 'chart-1' as const },
        { label: 'OCR', score: docStats.ocr_score, colorVar: 'chart-2' as const },
        { label: 'Parse', score: docStats.parse_score, colorVar: 'chart-3' as const },
        { label: 'Table', score: docStats.table_score, colorVar: 'chart-4' as const },
      ]

  const hasGrades = !!(docStats.mean_grade || docStats.low_grade)
  const hasDistribution =
    docStats.grade_distribution && Object.keys(docStats.grade_distribution).length > 0

  return (
    <TooltipProvider>
      <div className="space-y-4 p-0 md:p-4">
        {/* ── Score cards ── */}
        {hasConfidence && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {scoreCards.map(s => (
              <ScoreShapeCard key={s.label} label={s.label} score={s.score} colorVar={s.colorVar} />
            ))}
          </div>
        )}

        {/* ── Document-level overview ── */}
        <Card className="border-border bg-muted/50 p-4 space-y-4">
          {!isAudio && (
            <>
              {/* Primary counts */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                <StatBox
                  label="Pages"
                  value={fmt(docStats.total_pages)}
                  tip="Total number of pages extracted from the document."
                />
                <StatBox
                  label="Characters"
                  value={fmt(docStats.char_count)}
                  tip="Total character count across all extracted text."
                />
                <StatBox
                  label="Words"
                  value={fmt(docStats.word_count)}
                  tip="Total word count across all extracted text."
                />
                <StatBox
                  label="Tables"
                  value={fmt(docStats.table_count)}
                  tip="Number of tables detected in the document."
                />
                <StatBox
                  label="Images"
                  value={fmt(docStats.image_count)}
                  tip="Number of images or figures detected."
                />
              </div>

              {/* Secondary counts */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatBox
                  label="Section Headers"
                  value={fmt(docStats.section_header_count)}
                  tip="Number of section headings (H1–H3) found across all pages."
                />
                <StatBox
                  label="Paragraphs"
                  value={fmt(docStats.total_paragraphs)}
                  tip="Total paragraph blocks extracted across all pages."
                />
                <StatBox
                  label="Hyperlinks"
                  value={fmt(docStats.total_hyperlinks)}
                  tip="Number of hyperlinks detected in the extracted text."
                />
                <StatBox
                  label="Avg chars/page"
                  value={fmt(docStats.avg_chars_per_page)}
                  tip="Average number of characters per page. Low values may indicate sparse or image-heavy pages."
                />
              </div>
            </>
          )}

          {/* Audio (WhisperX) stats */}
          {isAudio && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatBox
                  label="Audio Duration"
                  value={formatDuration(visionStats?.audio_duration_seconds as number | null)}
                  tip="Length of the recording."
                />
                <StatBox
                  label="Processing Time"
                  value={formatDuration(visionStats?.processing_time_seconds as number | null)}
                  tip="Wall-clock time for the WhisperX transcription pass."
                />
                <StatBox
                  label="Real-time Factor"
                  value={
                    visionStats?.real_time_factor != null
                      ? `${(visionStats.real_time_factor as number).toFixed(2)}×`
                      : '—'
                  }
                  tip="Processing time ÷ audio length. Lower is faster — 0.5× means transcribed in half the audio length."
                />
                <StatBox
                  label="Language"
                  value={(visionStats?.detected_language as string | null) ?? '—'}
                  tip="ISO language code detected by WhisperX."
                />
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatBox
                  label="Segments"
                  value={fmt(visionStats?.segment_count as number | null)}
                  tip="Number of speech segments produced by WhisperX (one segment ≈ one utterance)."
                />
                <StatBox
                  label="Words"
                  value={fmt(docStats.word_count)}
                  tip="Total word count across the transcript."
                />
                <StatBox
                  label="Characters"
                  value={fmt(docStats.char_count)}
                  tip="Total character count in the speaker-tagged markdown."
                />
                <StatBox
                  label="Speakers"
                  value={fmt(visionStats?.speaker_count as number | null)}
                  tip="Distinct speakers identified by the diarization pass."
                />
              </div>
            </>
          )}

          {/* VisionLLM token / timing stats */}
          {isVisionLLM && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatBox
                label="Processing Time"
                value={
                  visionStats?.processing_time_seconds != null
                    ? `${(visionStats.processing_time_seconds as number).toFixed(1)}s`
                    : '—'
                }
                tip="Wall-clock time for the Vision LLM API call."
              />
              <StatBox
                label="Prompt Tokens"
                value={fmt(visionStats?.prompt_tokens as number | null)}
                tip="Tokens sent to the model (image + prompt text)."
              />
              <StatBox
                label="Output Tokens"
                value={fmt(visionStats?.completion_tokens as number | null)}
                tip="Tokens generated by the model in its response."
              />
              <StatBox
                label="Total Tokens"
                value={fmt(visionStats?.total_tokens as number | null)}
                tip="Total token usage for this extraction."
              />
            </div>
          )}

          {/* ── Grade + distribution (2-column) ── */}
          {(hasGrades || hasDistribution || docStats.content_types.length > 0) && (
            <>
              <Separator />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Column 1: Overall quality grades */}
                {hasGrades && (
                  <div className="space-y-2">
                    <p className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                      Overall Grade
                      <InfoTip text="Mean is the average quality across all pages. Low marks the worst-performing page — useful for spotting problem pages." />
                    </p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {docStats.mean_grade && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">Mean</span>
                          <GradeChip grade={docStats.mean_grade} />
                        </div>
                      )}
                      {docStats.low_grade && docStats.low_grade !== docStats.mean_grade && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground">Low</span>
                          <GradeChip grade={docStats.low_grade} />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Column 2: Grade distribution */}
                {hasDistribution && (
                  <div className="space-y-2">
                    <p className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                      Grade Distribution
                      <InfoTip text="Number of pages at each quality level. Excellent → Good → Fair → Poor. More Fair/Poor pages indicate extraction challenges." />
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {Object.entries(docStats.grade_distribution).map(([grade, count]) => (
                        <div key={grade} className="flex items-center gap-1">
                          <GradeChip grade={grade} />
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Content types — full width below the 2-col grid */}
              {docStats.content_types.length > 0 && (
                <div className="space-y-2">
                  <p className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                    Content Types
                    <InfoTip text="Types of content elements detected by the extraction pipeline (e.g. text, table, image, section header)." />
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {docStats.content_types.map(ct => (
                      <Badge key={ct} variant="outline" className="text-[10px] capitalize">
                        {ct.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* VisionLLM: model + detected type info */}
              {isVisionLLM && (visionStats?.vision_model || visionStats?.detected_type) && (
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {visionStats?.vision_model && (
                    <span>
                      Model:{' '}
                      <span className="text-muted-foreground font-mono text-[10px]">
                        {visionStats.vision_model as string}
                      </span>
                    </span>
                  )}
                  {visionStats?.detected_type && (
                    <span>
                      Type:{' '}
                      <span className="text-muted-foreground capitalize">
                        {formatDocumentType(visionStats.detected_type as string)}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Audio: model + device info */}
          {isAudio && (
            <>
              <Separator />
              <div className="space-y-2">
                {Array.isArray(visionStats?.speakers) &&
                  (visionStats.speakers as string[]).length > 0 && (
                    <div>
                      <p className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wide font-medium mb-1.5">
                        Speakers
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(visionStats.speakers as string[]).map(sp => (
                          <Badge key={sp} variant="outline" className="text-[10px] font-mono">
                            {sp}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {visionStats?.whisper_model && (
                    <span>
                      Model:{' '}
                      <span className="font-mono text-[10px]">
                        {visionStats.whisper_model as string}
                      </span>
                    </span>
                  )}
                  {visionStats?.whisper_device && (
                    <span>
                      Device:{' '}
                      <span className="font-mono text-[10px]">
                        {visionStats.whisper_device as string}
                        {visionStats?.whisper_compute_type
                          ? `/${visionStats.whisper_compute_type as string}`
                          : ''}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* ── Pages table ── */}
        {(docStats.total_pages ?? 0) > 0 && !isVisionLLM && !isAudio && (
          <Card className="border-border bg-muted/50">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">Per-page breakdown</span>
                <span className="text-xs text-muted-foreground/70">({pagesTotal} pages)</span>
              </div>
            </div>

            {pagesError && <p className="text-xs text-destructive p-4">{pagesError}</p>}

            {pagesLoading ? (
              <div className="space-y-1 p-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground">
                      <th className="text-left px-3 py-2 font-medium w-10">#</th>
                      <th className="text-right px-3 py-2 font-medium">Chars</th>
                      <th className="text-right px-3 py-2 font-medium">Words</th>
                      <th className="text-right px-3 py-2 font-medium">Tables</th>
                      <th className="text-right px-3 py-2 font-medium">Images</th>
                      <th className="text-right px-3 py-2 font-medium">Sections</th>
                      <th className="text-center px-3 py-2 font-medium">Grade</th>
                      <th className="text-center px-3 py-2 font-medium">Low</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map(p => (
                      <tr
                        key={p.id}
                        onClick={() => setSelectedPage(p.page_number)}
                        className="border-b border-border/50 hover:bg-muted/70 cursor-pointer transition-colors"
                      >
                        <td className="px-3 py-1.5 text-muted-foreground font-mono">
                          {p.page_number}
                        </td>
                        <td className="px-3 py-1.5 text-right text-foreground">
                          {fmt(p.char_count)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">
                          {fmt(p.word_count)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">
                          {fmt(p.table_count)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">
                          {fmt(p.image_count)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">
                          {fmt(p.section_header_count)}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <GradeChip grade={p.mean_grade} />
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <GradeChip grade={p.low_grade} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagesTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  Page {currentPage} of {pagesTotalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={currentPage <= 1 || pagesLoading}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={currentPage >= pagesTotalPages || pagesLoading}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* ── Page detail sidebar ── */}
        {selectedPage != null && (
          <PageDetailSidebar
            documentId={documentId}
            pageNumber={selectedPage}
            open={selectedPage != null}
            onClose={() => setSelectedPage(null)}
          />
        )}
      </div>
    </TooltipProvider>
  )
}
